
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { AlertTriangle, TrendingUp, TrendingDown, Users, Calendar, ChevronLeft, ChevronRight } from "lucide-react"
import { useState, useEffect, useMemo } from "react"
import { getOrgEqTrackerBatchPositivity } from "../../api/helpers"
import { getUser } from "../../api/helpers"
import { useDispatch } from "react-redux"

interface Notification {
  id: string
  type: "warning" | "positive" | "negative" | "info"
  title: string
  description: string
  theme: string
  timestamp: string
  team: string
  tracker?: string
  extendedIndex?: number
}

interface NotificationsFeedProps {
  organizationFilter?: string
  trackerFilter?: string
}

interface BatchPositivityResult {
  tracker_value_id: string
  tracker_id: string | null
  tracker_name: string
  top_emotion: {
    emotion: string
    score: number
    sentiment: string
  } | null
  summarized_reason: string | null
  content_type: string
  created_at: string
  orgemployee_id: string | null
  team_name: string | null
  positivity_score: "negative" | "neutral" | "positive"
  positivity_value: number
  positivity_scaled_percent: number
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "warning":
      return <AlertTriangle className="h-4 w-4 text-red-500" />
    case "positive":
      return <TrendingUp className="h-4 w-4 text-green-500" />
    case "negative":
      return <TrendingDown className="h-4 w-4 text-red-500" />
    default:
      return <Users className="h-4 w-4 text-blue-500" />
  }
}

const getNotificationColor = (type: string) => {
  switch (type) {
    case "warning":
      return "bg-red-50 border-red-200"
    case "positive":
      return "bg-green-50 border-green-200"
    case "negative":
      return "bg-red-50 border-red-200"
    default:
      return "bg-blue-50 border-blue-200"
  }
}

const getEmotionBadge = (emotionName: string | undefined, notificationType: string) => {
  // Get emotion label (capitalize first letter)
  const label = emotionName 
    ? emotionName.charAt(0).toUpperCase() + emotionName.slice(1) 
    : "Interest"
  
  // Match badge color to the notification box color
  switch (notificationType) {
    case "positive":
      return <Badge className="bg-green-100 text-green-800 border-green-200">{label}</Badge>
    case "negative":
    case "warning":
      return <Badge className="bg-red-100 text-red-800 border-red-200">{label}</Badge>
    default:
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">{label}</Badge>
  }
}

const getTeamDisplayName = (team: string) => {
  switch (team) {
    case "engineering":
      return "Engineering"
    case "product":
      return "Product"
    case "design":
      return "Design"
    case "support":
      return "Support"
    case "marketing":
      return "Marketing"
    case "sales":
      return "Sales"
    case "entire":
      return "Organization"
    default:
      return team.charAt(0).toUpperCase() + team.slice(1)
  }
}

// Helper function to map API data to Notification interface
const mapBatchPositivityToNotification = (result: BatchPositivityResult): Notification & { emotionName?: string } => {
  // Map positivity_score to notification type
  // Note: API now only returns 'positive' or 'negative' (neutral is filtered out)
  let type: "warning" | "positive" | "negative" | "info"
  if (result.positivity_score === "positive") {
    type = "positive"
  } else if (result.positivity_score === "negative") {
    type = "negative"
  } else {
    // Fallback (should not happen with new endpoint)
    type = "info"
  }

  // Construct title from tracker name only (cleaner, shorter)
  const title = result.tracker_name

  // Map team name to lowercase team identifier
  const teamLower = result.team_name?.toLowerCase() || "entire"

  return {
    id: result.tracker_value_id,
    type,
    title,
    description: result.summarized_reason || "No detailed description available",
    theme: result.tracker_name,
    timestamp: result.created_at,
    team: teamLower,
    tracker: result.tracker_id || undefined,
    emotionName: result.top_emotion?.emotion,
  }
}

export function NotificationsFeed({ organizationFilter = "entire", trackerFilter = "all" }: NotificationsFeedProps) {
  const dispatch = useDispatch()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [lastInteractionTime, setLastInteractionTime] = useState<number | null>(null)
  const [notifications, setNotifications] = useState<(Notification & { emotionName?: string })[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [organizationId, setOrganizationId] = useState<string | null>(null)

  // Fetch organization ID on mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await getUser(dispatch)
        if (userData?.user?.organization_info?._id) {
          setOrganizationId(userData.user.organization_info._id)
        }
      } catch (err) {
        console.error("Error loading user data:", err)
      }
    }
    loadUserData()
  }, [dispatch])

  // Fetch notifications when organizationId is available
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!organizationId) return

      setIsLoading(true)
      setError(null)

      try {
        const response = await getOrgEqTrackerBatchPositivity(organizationId, {
          hours: 240,
        })

        if (response?.results) {
          // API returns all positive/negative results, limit to 20 most intense on client
          const limitedResults = response.results.slice(0, 20)
          const mappedNotifications = limitedResults.map(mapBatchPositivityToNotification)
          setNotifications(mappedNotifications)
        } else {
          setNotifications([])
        }
      } catch (err) {
        console.error("Error fetching batch positivity:", err)
        setError("Failed to load notifications")
        setNotifications([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchNotifications()
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [organizationId])

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const matchesOrg = organizationFilter === "entire" || notification.team === organizationFilter
      const matchesTracker = trackerFilter === "all" || notification.tracker === trackerFilter
      return matchesOrg && matchesTracker
    })
  }, [notifications, organizationFilter, trackerFilter])

  useEffect(() => {
    setCurrentIndex(0)
  }, [organizationFilter, trackerFilter])

  useEffect(() => {
    if (!isPlaying || filteredNotifications.length === 0) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredNotifications.length)
    }, 4000) // Rotate every 4 seconds

    return () => clearInterval(interval)
  }, [isPlaying, filteredNotifications.length])

  useEffect(() => {
    if (lastInteractionTime === null) return

    const timeout = setTimeout(() => {
      setIsPlaying(true)
      setLastInteractionTime(null)
    }, 10000) // Resume after 10 seconds

    return () => clearTimeout(timeout)
  }, [lastInteractionTime])

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredNotifications.length)
    setIsPlaying(false)
    setLastInteractionTime(Date.now())
  }

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredNotifications.length) % filteredNotifications.length)
    setIsPlaying(false)
    setLastInteractionTime(Date.now())
  }

  const getExtendedNotifications = () => {
    if (filteredNotifications.length === 0) return []

    const extended = []
    const totalItems = filteredNotifications.length
    // Create 3 copies to ensure smooth infinite scrolling
    for (let i = -totalItems; i < totalItems * 2; i++) {
      const actualIndex = ((i % totalItems) + totalItems) % totalItems
      extended.push({ ...filteredNotifications[actualIndex], extendedIndex: i })
    }
    return extended
  }

  // Show loading state
  if (isLoading && notifications.length === 0) {
    return (
      <Card data-section="notifications">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-primary" />
            Key Moments Feed
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pt-1 pb-1">
          <div className="flex items-center justify-center h-[280px] text-muted-foreground">
            Loading key moments...
          </div>
        </CardContent>
      </Card>
    )
  }

  // Show error state
  if (error) {
    return (
      <Card data-section="notifications">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-primary" />
            Key Moments Feed
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pt-1 pb-1">
          <div className="flex items-center justify-center h-[280px] text-red-500">
            {error}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (filteredNotifications.length === 0) {
    return (
      <Card data-section="notifications">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-primary" />
            Key Moments Feed
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pt-1 pb-1">
          <div className="flex items-center justify-center h-[280px] text-muted-foreground">
            No key moments match the current filters
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-section="notifications">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="h-5 w-5 text-primary" />
          Key Moments Feed
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pt-1 pb-1">
        <div
          className="relative h-[280px] overflow-hidden"
          onMouseEnter={() => setIsPlaying(false)}
          onMouseLeave={() => setIsPlaying(true)}
        >
          <div className="flex items-center justify-center h-full">
            {getExtendedNotifications().map((notification, index) => {
              const offset = notification.extendedIndex - currentIndex
              const isCenter = offset === 0
              const isVisible = Math.abs(offset) <= 2

              if (!isVisible) return null

              return (
                <div
                  key={`${notification.id}-${notification.extendedIndex}`}
                  className={`absolute transition-all duration-500 ease-in-out ${
                    isCenter
                      ? "scale-100 opacity-100 z-10"
                      : Math.abs(offset) === 1
                        ? "scale-95 opacity-60 z-5"
                        : "scale-90 opacity-30 z-0"
                  }`}
                  style={{
                    transform: `translateX(${offset * 380}px) scale(${isCenter ? 1 : Math.abs(offset) === 1 ? 0.95 : 0.9})`,
                    width: "360px",
                  }}
                >
                  <div className={`p-5 rounded-lg border ${getNotificationColor(notification.type)} h-[260px]`}>
                    <div className="flex items-start gap-3 h-full">
                      <div className="flex-shrink-0 mt-0.5">{getNotificationIcon(notification.type)}</div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="font-medium text-sm leading-tight">{notification.title}</h4>
                        </div>
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {getEmotionBadge((notification as any).emotionName, notification.type)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-5 flex-1">
                          {notification.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto">
                          <span className="flex items-center gap-1 truncate">
                            <Users className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{getTeamDisplayName(notification.team)}</span>
                          </span>
                          <span className="flex-shrink-0 ml-2">{notification.timestamp}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="absolute inset-y-0 left-0 flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPrev}
              className="h-16 w-16 p-0 bg-background/80 hover:bg-background rounded-full"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToNext}
              className="h-16 w-16 p-0 bg-background/80 hover:bg-background rounded-full"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
